/**
 * Login-flow redirect elements that preserve the intended
 * deep-link target (see lib/returnTo.ts for the mechanism + guard).
 *
 * `RedirectToLogin` replaces the bare `<Navigate to="/login">` in
 * App.tsx's UNAUTHENTICATED catch-all: it captures the requested
 * location (path + search + hash) at the redirect moment, then
 * redirects. `PostLoginRedirect` replaces the bare `<Navigate to="/">`
 * on the AUTHENTICATED `/login` route: once auth flips while the URL
 * is still `/login` (the password-form flow — useLoginForm relies on
 * App re-rendering rather than navigating), it lands on the preserved
 * target, defaulting to home. The SSO flow resolves on
 * pages/AuthCallback.tsx instead (URL is `/auth/callback` when auth
 * flips there, so this route never sees it).
 */
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { clearReturnTo, peekReturnTo, rememberReturnTo } from '@/lib/returnTo';

/**
 * Unauthenticated catch-all: remember where the user was headed, then
 * bounce to the login screen.
 */
export function RedirectToLogin() {
  const location = useLocation();
  const target = location.pathname + location.search + location.hash;

  // Effect, not render: writing storage during render is impure and
  // would double-fire under StrictMode. The captured `target` string is
  // from THIS render, so ordering with <Navigate>'s own effect (child
  // effects run first) cannot corrupt it.
  useEffect(() => {
    rememberReturnTo(target);
  }, [target]);

  return <Navigate to="/login" replace />;
}

/**
 * Authenticated `/login` route: land on the preserved target (or home).
 * Peek during render / clear in an effect — a render-time consume would
 * lose the target if React discards the render (StrictMode, Suspense).
 * The clear is idempotent, and <Navigate> fires exactly once per mount,
 * so navigation happens once regardless of effect double-invocation.
 */
export function PostLoginRedirect() {
  const target = peekReturnTo();

  useEffect(() => {
    clearReturnTo();
  }, []);

  return <Navigate to={target ?? '/'} replace />;
}
